export type DiffSegment = { type: 'equal' | 'add' | 'del'; value: string };

/**
 * Splits on word/whitespace boundaries, keeping whitespace runs as their own
 * tokens so the segments produced by `diffWords` rejoin losslessly (spacing
 * and newlines survive the round trip untouched).
 */
function tokenize(text: string): string[] {
  return text.match(/\s+|\S+/g) ?? [];
}

/**
 * Classic LCS word diff — fine for the short, prose-length strings this is
 * built for (a caption before/after a rewrite), not a code-scale differ.
 * Adjacent same-type tokens are merged into one segment so the renderer
 * doesn't fragment a sentence into a span per word.
 */
export function diffWords(oldText: string, newText: string): DiffSegment[] {
  const a = tokenize(oldText);
  const b = tokenize(newText);
  const n = a.length;
  const m = b.length;

  const lcs: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      lcs[i][j] = a[i] === b[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }

  const segments: DiffSegment[] = [];
  const push = (type: DiffSegment['type'], value: string) => {
    const last = segments[segments.length - 1];
    if (last && last.type === type) {
      last.value += value;
    } else {
      segments.push({ type, value });
    }
  };

  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      push('equal', a[i]);
      i++;
      j++;
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      push('del', a[i]);
      i++;
    } else {
      push('add', b[j]);
      j++;
    }
  }
  while (i < n) {
    push('del', a[i]);
    i++;
  }
  while (j < m) {
    push('add', b[j]);
    j++;
  }
  return segments;
}
